import {
	createTextArg,
	createSelectArg,
	ArgCondition,
} from './controlsHelpers';
import { IsharesButtonType } from '../components/ishares/web-components/_types/button';
import { IsharesStandardBackgroundColor } from '../types/colors';
import { buttonColors } from './colors';
import { FontAwesomeIcon, FontAwesomeIcons } from './icons';

const ctaCategory = 'CTA (optional)';
const secondaryCtaCategory = 'Secondary CTA (optional)';

export type ModesOfOpening = '_self' | '_blank';

export const modesOfOpening: { [indes: string]: ModesOfOpening } = {
	'Current Window': '_self',
	'New Window': '_blank',
};

export type CtaStyle =
	| 'ishares-primary'
	| 'ishares-secondary'
	| 'ishares-tertiary';

export type SecondaryCtaStyle = Omit<CtaStyle, 'ishares-primary'>;

export const ctaStyles: { [index: string]: CtaStyle } = {
	'iShares DS Primary': 'ishares-primary',
	'iShares DS Secondary': 'ishares-secondary',
	'iShares DS Tertiary': 'ishares-tertiary',
};

export const secondaryCtaStyles: { [index: string]: CtaStyle } = {
	'iShares DS Secondary': 'ishares-secondary',
	'iShares DS Tertiary': 'ishares-tertiary',
};

export type CTAFields =
	| 'label'
	| 'url'
	| 'mode'
	| 'ariaLabel'
	| 'linkTitle'
	| 'style'
	| 'buttonBg'
	| 'icon';

interface CreateCTAArgsTypesOptions {
	category?: string;
	subcategory?: string;
	condition?: ArgCondition;
	excludeFields?: CTAFields[];
	disabled?: boolean;
	identifier?: string;
}

export const icons: { [index: string]: FontAwesomeIcon | '' } = {
	...FontAwesomeIcons,
	None: '',
};

export const createCtaArgTypes = (
	{
		category = ctaCategory,
		subcategory,
		condition,
		excludeFields = [],
		disabled = false,
	}: CreateCTAArgsTypesOptions = {
		category: ctaCategory,
		excludeFields: [],
	}
) => {
	return {
		'cta.label': createTextArg({
			description: 'Label *',
			name: 'Label',
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('label'),
		}),
		'cta.url': createTextArg({
			description: 'URL *',
			name: 'URL',
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('url'),
		}),
		'cta.mode': createSelectArg({
			description: 'Link Opening Mode *',
			name: 'Link Opening Mode',
			options: modesOfOpening,
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('mode'),
		}),
		'cta.ariaLabel': createTextArg({
			description: 'ARIA Label *',
			name: 'ARIA Label',
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('ariaLabel'),
		}),
		'cta.linkTitle': createTextArg({
			description: 'Link Title',
			name: 'Link Title',
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('linkTitle'),
		}),
		'cta.style': createSelectArg({
			description: 'CTA Style',
			name: 'CTA Style',
			options: ctaStyles,
			category,
			subcategory,
			condition,
			disabled: disabled || excludeFields.includes('style'),
		}),
		'cta.buttonBg': createSelectArg({
			description: 'CTA Button Background',
			name: 'CTA Button Background',
			options: buttonColors,
			category,
			subcategory,
			condition: condition,
			disabled: disabled || excludeFields.includes('buttonBg'),
		}),
		'cta.icon': createSelectArg({
			description: 'CTA Icon',
			name: 'CTA Icon',
			options: icons,
			category,
			subcategory,
			condition,
			disabled: disabled || excludeFields.includes('icon'),
		}),
	};
};

interface DefaultCtaArgsOptions {
	label?: string;
	url?: string;
	mode?: '_self' | '_blank';
	ariaLabel?: string;
	linkTitle?: string;
	buttonBg?: IsharesStandardBackgroundColor;
	icon?: FontAwesomeIcon;
}

interface CreateCtaArgsOptions extends DefaultCtaArgsOptions {
	style?: CtaStyle;
}

export type CallToAction = CreateCtaArgsOptions;

interface CreateSecondaryCtaArgsOptions extends DefaultCtaArgsOptions {
	style?: SecondaryCtaStyle;
}

export type SecondaryCallToAction = CreateSecondaryCtaArgsOptions;

export interface CtaArgs {
	'cta.label': string;
	'cta.url': string;
	'cta.mode': '_self' | '_blank';
	'cta.ariaLabel': string;
	'cta.linkTitle': string;
	'cta.buttonBg'?: string;
	'cta.style'?: CtaStyle;
	'cta.icon'?: FontAwesomeIcon | '';
}

export const createCtaArgs = ({
	label,
	url,
	mode,
	ariaLabel,
	linkTitle,
	buttonBg,
	style,
	icon,
}: CreateCtaArgsOptions = {}): CtaArgs => {
	return {
		'cta.label': label ?? 'TAP INTO FACTORS',
		'cta.url': url ?? 'http://',
		'cta.mode': mode ?? '_blank',
		'cta.ariaLabel': ariaLabel ?? '',
		'cta.linkTitle': linkTitle ?? '',
		'cta.buttonBg': buttonBg ?? 'white',
		'cta.style': style ?? 'ishares-primary',
		'cta.icon': icon ?? '',
	};
};

export const createSecondaryCtaArgTypes = (
	{
		category = secondaryCtaCategory,
		subcategory,
		condition,
		excludeFields = [],
		disabled = false,
	}: CreateCTAArgsTypesOptions = {
		category: secondaryCtaCategory,
		excludeFields: [],
	}
) => {
	return {
		'secondaryCta.label': createTextArg({
			description: 'Label *',
			name: 'Label',
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('label'),
		}),
		'secondaryCta.url': createTextArg({
			description: 'URL *',
			name: 'URL',
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('url'),
		}),
		'secondaryCta.mode': createSelectArg({
			description: 'Link Opening Mode *',
			name: 'Link Opening Mode',
			options: modesOfOpening,
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('mode'),
		}),
		'secondaryCta.ariaLabel': createTextArg({
			description: 'ARIA Label *',
			name: 'ARIA Label',
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('ariaLabel'),
		}),
		'secondaryCta.linkTitle': createTextArg({
			description: 'Link Title',
			name: 'Link Title',
			category,
			subcategory,
			condition,
			required: true,
			disabled: disabled || excludeFields.includes('linkTitle'),
		}),
		'secondaryCta.style': createSelectArg({
			description: 'CTA Style',
			name: 'CTA Style',
			options: secondaryCtaStyles,
			category,
			subcategory,
			condition,
			disabled: disabled || excludeFields.includes('style'),
		}),
		'secondaryCta.icon': createSelectArg({
			description: 'CTA Icon',
			name: 'CTA Icon',
			options: icons,
			category,
			subcategory,
			condition,
			disabled: disabled || excludeFields.includes('icon'),
		}),
	};
};

export interface SecondaryCtaArgs {
	'secondaryCta.label': string;
	'secondaryCta.url': string;
	'secondaryCta.mode': '_self' | '_blank';
	'secondaryCta.ariaLabel': string;
	'secondaryCta.linkTitle': string;
	'secondaryCta.style': SecondaryCtaStyle;
	'secondaryCta.icon'?: FontAwesomeIcon | '';
}

export const createSecondaryCtaArgs = ({
	label,
	url,
	mode,
	ariaLabel,
	linkTitle,
	style,
	icon,
}: CreateSecondaryCtaArgsOptions = {}): SecondaryCtaArgs => {
	return {
		'secondaryCta.label': label ?? '',
		'secondaryCta.url': url ?? 'http://',
		'secondaryCta.mode': mode ?? '_blank',
		'secondaryCta.ariaLabel': ariaLabel ?? '',
		'secondaryCta.linkTitle': linkTitle ?? '',
		'secondaryCta.style': style ?? 'ishares-secondary',
		'secondaryCta.icon': icon ?? '',
	};
};

export const getButtonTypeFromCtaStyle = (
	ctaStyle: CtaStyle | SecondaryCtaStyle
): IsharesButtonType => {
	if (ctaStyle) {
		return ctaStyle.replace('ishares-', '') as IsharesButtonType;
	}
};
// it's only used by video-content-legacy.component.ts
// should be removed later
export const getButtonAttributesFromCta = (cta: CallToAction): string => {
	const type: IsharesButtonType = getButtonTypeFromCtaStyle(cta.style);
	const color =
		type === 'primary' ? (cta.buttonBg ? cta.buttonBg : 'white') : null;
	return color ? `type=${type} color=${color}` : `type=${type}`;
};
